import {
  type HubExampleStream,
  type HubExampleStreamDefinitionType,
  type HubExampleStreamRunningTask,
  HubExampleStreamStatus,
} from '@robovision/hub'
import { i18n } from '~i18n'
import {
  CameraConfigurationCameraData,
  CameraConfigurationData,
  CameraConfigurationDatapoolData,
} from './CameraConfigurationData'
import {
  CameraConfigurationCameraKind,
  CameraConfigurationDatapoolKind,
  CameraConfigurationKind,
  CameraConfigurationKindIds,
} from './CameraConfigurationKind'
import {
  ExampleStreamViewTemplates,
  MinMaxCardinality,
  ViewTemplate,
} from './ViewTemplate'


interface CameraConfigurationParams {
  config: CameraConfigurationKind
  createdAt: Date
  definitionId: string
  definitionName?: string
  definitionType: HubExampleStreamDefinitionType
  disabled: boolean
  id: string
  name: string
  parentExampleStreamId?: string
  runningTask?: HubExampleStreamRunningTask
  status: string
  statusUpdatedAt: Date
  valid?: boolean
  viewTemplates?: ViewTemplate[]
}

export class CameraConfiguration {
  static readonly modelName = 'CameraConfiguration'

  public config: CameraConfigurationKind
  public createdAt: Date
  public definitionId: string
  public definitionName?: string
  public definitionType: HubExampleStreamDefinitionType
  public disabled: boolean
  public id: string
  public name: string
  public parentExampleStreamId?: string
  public runningTask?: HubExampleStreamRunningTask
  public status: string
  public statusUpdatedAt: Date
  public valid?: boolean
  public viewTemplates: ViewTemplate[]

  constructor (params: CameraConfigurationParams) {
    this.config = params.config
    this.createdAt = params.createdAt
    this.definitionId = params.definitionId
    this.definitionName = params.definitionName
    this.definitionType = params.definitionType
    this.disabled = params.disabled
    this.id = params.id
    this.name = params.name
    this.parentExampleStreamId = params.parentExampleStreamId
    this.runningTask = params.runningTask
    this.status = params.status
    this.statusUpdatedAt = params.statusUpdatedAt
    this.valid = params.valid
    this.viewTemplates = params.viewTemplates ?? []
  }

  public get isActive (): boolean {
    return this.status === HubExampleStreamStatus.VALIDATING ||
      this.status === HubExampleStreamStatus.INFERENCE ||
      this.status === HubExampleStreamStatus.IN_USE ||
      this.status === HubExampleStreamStatus.RECORDING
  }

  public get isValid (): boolean {
    return this.valid ?? false
  }

  public get isValidating (): boolean {
    return this.status === HubExampleStreamStatus.VALIDATING
  }

  public get kind (): CameraConfigurationKindIds {
    return this.config.kind
  }

  public get cameraTypeLabel (): string {
    switch (this.kind) {
      case CameraConfigurationKindIds.Datapool:
        return i18n.t('cameras.types.emulator')
      case CameraConfigurationKindIds.Camera:
        return i18n.t('cameras.types.physicalCamera')
      default:
        return i18n.t('cameras.types.unknown', 'Unknown')
    }
  }

  public get statusLabel (): string {
    switch (this.status) {
      case HubExampleStreamStatus.IDLE:
        return i18n.t('cameras.titles.idle')
      case HubExampleStreamStatus.INFERENCE:
        return i18n.t('cameras.titles.inference')
      case HubExampleStreamStatus.IN_USE:
        return i18n.t('cameras.titles.inUse')
      case HubExampleStreamStatus.RECORDING:
        return i18n.t('cameras.titles.recording')
      case HubExampleStreamStatus.VALIDATING:
        return i18n.t('cameras.titles.validating')
      case HubExampleStreamStatus.INVALID:
        return i18n.t('cameras.titles.invalid')
      default:
        return i18n.t('cameras.titles.notAvailable')
    }
  }

  /**
   * Creates a CameraConfiguration instance from a raw HubAlphaExampleStream response object.
   * Note: ViewTemplates are not part of HubAlphaExampleStream itself.
   * They should be fetched separately and populated using `setViewTemplatesFromResponse`.
   * @param data The raw HubAlphaExampleStream response object from the API.
   * @returns A new CameraConfiguration instance.
   */
  public static fromResponse (data: HubExampleStream): CameraConfiguration {
    const params: CameraConfigurationParams = {
      config: CameraConfigurationKind.fromResponse(data.config),
      createdAt: new Date(data.createdAt),
      definitionId: data.definitionId,
      definitionName: data.definitionName,
      definitionType: data.definitionType,
      disabled: data.disabled,
      id: data.id,
      name: data.name,
      parentExampleStreamId: data.parentExampleStreamId,
      runningTask: data.runningTask,
      status: data.status,
      statusUpdatedAt: new Date(data.statusUpdatedAt),
      valid: data.valid,
    }

    return new CameraConfiguration(params)
  }

  /**
   * Populates the view templates for this configuration using data typically fetched
   * based on `definitionId` and `definitionType`.
   * @param viewTemplates The ExampleStreamViewTemplates class.
   */
  public setViewTemplates (viewTemplates: ExampleStreamViewTemplates | undefined): void {
    if (!viewTemplates) {
      this.viewTemplates = []
      return
    }

    if (this.definitionType === viewTemplates.definitionType && this.definitionId === viewTemplates.definitionId) {
      const mappedDefinitionTemplates = ExampleStreamViewTemplates.fromResponse(viewTemplates)
      this.viewTemplates = mappedDefinitionTemplates.viewTemplates
      this.definitionName = mappedDefinitionTemplates.definitionName
    } else {
      console.warn(
        `Attempted to set view templates with mismatched definitionId and definitionType.
        Expected ${this.definitionId} and ${this.definitionType},
        got ${viewTemplates.definitionId} and ${viewTemplates.definitionType}.
        View templates will not be updated.`,
      )
    }
  }

  /**
   * Calculates the aggregated minimum and maximum number of cameras required or allowed
   * by the view templates associated with this configuration.
   * Returns null if no view templates are defined.
   * If any template has an undefined maximum, the total maximum will also be undefined.
   */
  public getRequiredCamerasCount (): MinMaxCardinality | null {
    if (!this.viewTemplates || this.viewTemplates.length === 0) {
      return null
    }

    let totalMin = 0
    let totalMax: number | undefined = 0

    for (const template of this.viewTemplates) {
      totalMin += template.minInstances
      if (totalMax !== undefined) {
        const templateMax = template.maxInstances
        if (templateMax === undefined) {
          totalMax = undefined
        } else {
          totalMax += templateMax
        }
      }
    }
    return {
      max: totalMax,
      min: totalMin,
    }
  }

  public toData (): CameraConfigurationData {
    if (this.config instanceof CameraConfigurationCameraKind) {
      return new CameraConfigurationCameraData({
        configuredCameras: this.config.toData(),
        definitionId: this.definitionId,
        definitionType: this.definitionType,
        name: this.name,
        triggerMode: this.config.triggerMode,
      })
    } else if (this.config instanceof CameraConfigurationDatapoolKind) {
      return new CameraConfigurationDatapoolData({
        datapoolId: this.config.datapoolId,
        definitionId: this.definitionId,
        definitionType: this.definitionType,
        name: this.name,
        sampleFilters: this.config.sampleFilters,
        simulatedFps: this.config.simulatedFps,
      })
    }
    throw new Error('Invalid camera configuration kind')
  }

  public toExampleStream (): HubExampleStream {
    return {
      config: this.config.toExampleStream(),
      createdAt: this.createdAt.toISOString(),
      definitionId: this.definitionId,
      definitionName: this.definitionName,
      definitionType: this.definitionType,
      disabled: this.disabled,
      id: this.id,
      name: this.name,
      status: this.status as HubExampleStreamStatus,
      statusUpdatedAt: this.statusUpdatedAt.toISOString(),
      valid: this.valid,
    }
  }
}
